// src/services/authApi.ts

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { User } from '../types/models/user'
import { RootState } from '../store'
import { PASSWORD_RESET_URL, REFRESH_TOKEN_URL, USER_DETAIL_URL, LOGIN_URL } from '../constants'
import { LoginCredentials, LoginResponse, OkResp, ResetPasswordData } from '../types/api/responses'

export const authApi = createApi({
  reducerPath: 'authApi',

  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_ENDPOINT || 'https://go-prod.agileview-ai.com',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.accessToken
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      headers.set('Cache-Control', 'no-cache')

      return headers
    },
    credentials: 'include',
  }),

  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginCredentials>({
      query: (credentials) => ({
        url: LOGIN_URL,
        method: 'POST',
        body: credentials,
        credentials: 'include',
      }),
    }),

    fetchUserProfile: builder.query<User, void>({
      query: () => USER_DETAIL_URL,
    }),

    requestPasswordReset: builder.mutation<OkResp, string>({
      query: (email) => ({
        url: PASSWORD_RESET_URL,
        method: 'GET',
        params: { email },
      }),
    }),

    resetPassword: builder.mutation<void, ResetPasswordData>({
      query: (resetData) => ({
        url: PASSWORD_RESET_URL,
        method: 'POST',
        body: resetData,
      }),
    }),

    refreshToken: builder.mutation<LoginResponse, string>({
      query: (refreshToken) => ({
        url: REFRESH_TOKEN_URL,
        method: 'POST',
        body: { refresh_token: refreshToken },
      }),
    }),
  }),
})

export const {
  useLoginMutation,
  useFetchUserProfileQuery,
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
  useRefreshTokenMutation,
} = authApi
